import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useProjects } from '../hooks/useProjects';
import { Project, ProjectFilters } from '../types/project';
import CommonPageHeader from '../components/common/CommonPageHeader';
import SearchBar from '../components/common/SearchBar';
import ProjectList from '../components/projects/ProjectList';
import ProjectForm from '../components/projects/ProjectForm';
import ProjectView from '../components/projects/ProjectView';
import LoadingScreen from '../components/common/LoadingScreen';
import { 
  PlusIcon, 
  ChartBarIcon,
  FunnelIcon,
  ArrowPathIcon,
  FolderIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '../components/common/Card';

const Projects: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const {
    projects,
    loading,
    createProject,
    updateProject,
    deleteProject,
    updateProjectStatus,
    updateProjectPriority,
    getProjectStats
  } = useProjects();

  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [viewingProject, setViewingProject] = useState<Project | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ProjectFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  // Effect to handle FAB trigger for new project
  useEffect(() => {
    const handleFABNewProject = () => {
      setEditingProject(null);
      setShowForm(true);
    };

    window.addEventListener('openProjectForm', handleFABNewProject);
    return () => {
      window.removeEventListener('openProjectForm', handleFABNewProject);
    };
  }, []);

  // Jogosultság ellenőrzés
  if (!hasAccess('projects')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.errors.loadingFailed')}</div>
      </div>
    );
  }

  // Projektek szűrése
  const filteredProjects = projects.filter(project => {
    // Szöveges keresés
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        project.name.toLowerCase().includes(searchLower) ||
        project.description.toLowerCase().includes(searchLower) ||
        project.customerName?.toLowerCase().includes(searchLower) ||
        project.tags.some(tag => tag.toLowerCase().includes(searchLower));
      
      if (!matchesSearch) return false;
    }

    // Státusz szűrés
    if (filters.status && project.status !== filters.status) return false;

    // Prioritás szűrés
    if (filters.priority && project.priority !== filters.priority) return false;

    // Hozzárendelt felhasználó szűrés
    if (filters.assignedUser && !project.assignedUsers.includes(filters.assignedUser)) return false;

    // Ügyfél szűrés
    if (filters.customerId && project.customerId !== filters.customerId) return false;

    return true;
  });

  // Projekt létrehozás
  const handleCreateProject = async (projectData: any) => {
    try {
      await createProject(projectData);
      setShowForm(false);
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  };

  // Projekt frissítés
  const handleUpdateProject = async (projectData: any) => {
    if (!editingProject) return;
    
    try {
      await updateProject(editingProject.id, projectData);
      setEditingProject(null);
      setShowForm(false);
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  };

  // Projekt törlés
  const handleDeleteProject = async (projectId: string) => {
    if (window.confirm(t('projects.confirmDelete') || 'Biztosan törölni szeretné ezt a projektet?')) {
      try {
        await deleteProject(projectId);
      } catch (error) {
        console.error('Error deleting project:', error);
        alert(t('projects.deleteError') || 'Hiba történt a projekt törlése során');
      }
    }
  };

  // Projekt szerkesztés
  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setShowForm(true);
  };

  // Projekt megtekintés
  const handleViewProject = (project: Project) => {
    setViewingProject(project);
  };

  // Státusz frissítés
  const handleUpdateStatus = async (projectId: string, status: Project['status']) => {
    try {
      await updateProjectStatus(projectId, status);
      toast.success(t('projects.messages.statusUpdated') || 'Státusz sikeresen frissítve!');
    } catch (error) {
      console.error('Error updating project status:', error);
      toast.error(t('projects.messages.statusUpdateError') || 'Hiba történt a státusz frissítése során');
    }
  };

  // Prioritás frissítés
  const handleUpdatePriority = async (projectId: string, priority: Project['priority']) => {
    try {
      await updateProjectPriority(projectId, priority);
      toast.success(t('projects.messages.priorityUpdated') || 'Prioritás sikeresen frissítve!');
    } catch (error) {
      console.error('Error updating project priority:', error);
      toast.error(t('projects.messages.priorityUpdateError') || 'Hiba történt a prioritás frissítése során');
    }
  };

  // Szűrők resetelése
  const resetFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  // Statisztikák
  const stats = getProjectStats();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <CommonPageHeader
        title={t('projects.title') || 'Projektek'}
        subtitle={t('projects.subtitle') || 'Projektjeinek kezelése és nyomon követése'}
        icon={ChartBarIcon}
      >
        <button
          onClick={() => {
            setEditingProject(null);
            setShowForm(true);
          }}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>{t('projects.new') || 'Új projekt'}</span>
        </button>
      </CommonPageHeader>

      <div className="w-full px-4 py-4">
        {/* Statisztikák */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
          <Card>
            <CardContent className="p-2 md:p-4">
              <div className="flex items-center gap-2 md:gap-4">
                <div className="flex-shrink-0">
                  <FolderIcon className="w-5 h-5 md:w-8 md:h-8 text-gray-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] md:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{t('projects.stats.total') || 'Összes projekt'}</div>
                  <div className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-2 md:p-4">
              <div className="flex items-center gap-2 md:gap-4">
                <div className="flex-shrink-0">
                  <PlayIcon className="w-5 h-5 md:w-8 md:h-8 text-blue-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] md:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{t('projects.stats.active') || 'Aktív'}</div>
                  <div className="text-lg md:text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.active}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-2 md:p-4">
              <div className="flex items-center gap-2 md:gap-4">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="w-5 h-5 md:w-8 md:h-8 text-green-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] md:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{t('projects.stats.completed') || 'Befejezett'}</div>
                  <div className="text-lg md:text-2xl font-bold text-green-600 dark:text-green-400">{stats.completed}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-2 md:p-4">
              <div className="flex items-center gap-2 md:gap-4">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="w-5 h-5 md:w-8 md:h-8 text-red-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] md:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{t('projects.stats.overdue') || 'Lejárt'}</div>
                  <div className="text-lg md:text-2xl font-bold text-red-600 dark:text-red-400">{stats.overdueCount}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Keresés és szűrők — egy sorban */}
        <div className="flex flex-wrap items-center justify-between gap-2 md:gap-3 mb-4">
          <div className="w-full sm:flex-1 sm:min-w-[200px] sm:max-w-md">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('projects.searchPlaceholder') || 'Keresés projektekben...'}
            />
          </div>

          <div className="flex items-center gap-2 min-w-0">
            {/* Aktív szűrő badge-ek */}
            {filters.status && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 whitespace-nowrap">
                {t(`projects.status.${filters.status}`) || filters.status}
                <button onClick={() => setFilters(prev => ({ ...prev, status: undefined }))} className="ml-1 hover:text-blue-900 dark:hover:text-blue-200">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.priority && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 whitespace-nowrap">
                {t(`projects.priority.${filters.priority}`) || filters.priority}
                <button onClick={() => setFilters(prev => ({ ...prev, priority: undefined }))} className="ml-1 hover:text-purple-900 dark:hover:text-purple-200">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}

            {/* Szűrők gomb */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-all ${
                showFilters
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 border border-gray-200 dark:border-gray-700'
              }`}
            >
              <FunnelIcon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t('common.filters') || 'Szűrők'}</span>
            </button>

            {/* Reset gomb — csak ha van aktív szűrő */}
            {(filters.status || filters.priority || searchTerm) && (
              <button
                onClick={resetFilters}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors whitespace-nowrap"
              >
                <span className="hidden md:inline">{t('common.reset') || 'Törlés'}</span>
                <ArrowPathIcon className="h-4 w-4 md:hidden" />
              </button>
            )}
          </div>
        </div>

        {/* Szűrők panel */}
        {showFilters && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 md:p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('projects.fields.status') || 'Státusz'}
                </label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as Project['status'] || undefined }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">{t('common.all') || 'Minden'}</option>
                  <option value="planning">{t('projects.status.planning') || 'Tervezés'}</option>
                  <option value="active">{t('projects.status.active') || 'Aktív'}</option>
                  <option value="completed">{t('projects.status.completed') || 'Befejezett'}</option>
                  <option value="paused">{t('projects.status.paused') || 'Szünetel'}</option>
                  <option value="cancelled">{t('projects.status.cancelled') || 'Megszakítva'}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('projects.fields.priority') || 'Prioritás'}
                </label>
                <select
                  value={filters.priority || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value as Project['priority'] || undefined }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">{t('common.all') || 'Minden'}</option>
                  <option value="low">{t('projects.priority.low') || 'Alacsony'}</option>
                  <option value="medium">{t('projects.priority.medium') || 'Közepes'}</option>
                  <option value="high">{t('projects.priority.high') || 'Magas'}</option>
                  <option value="urgent">{t('projects.priority.urgent') || 'Sürgős'}</option>
                </select>
              </div>

              <div className="flex items-end">
                <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                  {filteredProjects.length} / {projects.length} {t('projects.resultsCount') || 'projekt'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Projektek listája */}
        <ProjectList
          projects={filteredProjects}
          loading={loading}
          onEdit={handleEditProject}
          onDelete={handleDeleteProject}
          onView={handleViewProject}
          onUpdateStatus={handleUpdateStatus}
          onUpdatePriority={handleUpdatePriority}
        />
      </div>

      {/* Projekt form modal */}
      <ProjectForm
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingProject(null);
        }}
        onSubmit={editingProject ? handleUpdateProject : handleCreateProject}
        initialData={editingProject || undefined}
      />

      {/* Projekt view modal */}
      {viewingProject && (
        <ProjectView
          project={viewingProject}
          isOpen={Boolean(viewingProject)}
          onClose={() => setViewingProject(null)}
        />
      )}
    </div>
  );
};

export default Projects; 